/**
 * 页面埋点主入口：组装公共能力、会话模块、页面浏览模块（含定时心跳与停留检测）、页面跳转模块、元素曝光模块、元素点击模块。
 * 依赖：
 * - window.pageTrackingUtils
 * - window.createPageTrackingCore
 * - window.createPageTrackingSession
 * - window.createPageTrackingPageView
 * - window.createPageTrackingPageJump
 * - window.createPageTrackingElementExposure
 * - window.createPageTrackingElementClick
 * - window.createPageTrackingSearch
 * - window.createPageTrackingMediaInteraction
 * - window.createPageTrackingPopupExposure
 * - window.createPageTrackingCommerceInteraction（仅购物车优惠券结果上报，无文档点击监听）
 * - window.pageTrackingConfig
 * - window.pageTrackingContext
 */
(function initializePageTracking(window, document) {
  const config = window.pageTrackingConfig || {};
  const trackingContext = window.pageTrackingContext || {};
  const utils = window.pageTrackingUtils;
  const pageTracking = window.pageTracking || {};
  const createPageTrackingCore = window.createPageTrackingCore;
  const createPageTrackingSession = window.createPageTrackingSession;
  const createPageTrackingPageView = window.createPageTrackingPageView;
  const createPageTrackingPageJump = window.createPageTrackingPageJump;
  const createPageTrackingElementExposure = window.createPageTrackingElementExposure;
  const createPageTrackingElementClick = window.createPageTrackingElementClick;
  const createPageTrackingSearch = window.createPageTrackingSearch;
  const createPageTrackingMediaInteraction = window.createPageTrackingMediaInteraction;
  const createPageTrackingPopupExposure = window.createPageTrackingPopupExposure;
  const createPageTrackingCommerceInteraction = window.createPageTrackingCommerceInteraction;
  const consentChangedEventName = 'pageTrackingConsentChanged';

  function createConsentChangedEvent(granted) {
    if (typeof window.CustomEvent === 'function') {
      return new window.CustomEvent(consentChangedEventName, {
        detail: {
          granted: Boolean(granted)
        }
      });
    }

    return {
      type: consentChangedEventName,
      detail: {
        granted: Boolean(granted)
      }
    };
  }

  function dispatchConsentChanged(granted) {
    document.dispatchEvent(createConsentChangedEvent(granted));
  }

  function resolveTrackingConsentGranted() {
    const shopifyObject = window.Shopify;
    const customerPrivacy = shopifyObject && shopifyObject.customerPrivacy;

    if (!customerPrivacy || typeof customerPrivacy.userCanBeTracked !== 'function') {
      return false;
    }

    try {
      return customerPrivacy.userCanBeTracked() === true;
    } catch (error) {
      return false;
    }
  }

  function hasRequiredDependencies() {
    return Boolean(
      utils &&
      typeof createPageTrackingCore === 'function' &&
      typeof createPageTrackingSession === 'function' &&
      typeof createPageTrackingPageView === 'function' &&
      typeof createPageTrackingPageJump === 'function' &&
      typeof createPageTrackingElementExposure === 'function' &&
      typeof createPageTrackingElementClick === 'function' &&
      typeof createPageTrackingSearch === 'function' &&
      typeof createPageTrackingMediaInteraction === 'function' &&
      typeof createPageTrackingPopupExposure === 'function' &&
      typeof createPageTrackingCommerceInteraction === 'function'
    );
  }

  function ensureConsentTrackingApiLoaded() {
    const shopifyObject = window.Shopify;

    if (
      pageTracking.consentApiRequested ||
      !shopifyObject ||
      typeof shopifyObject.loadFeatures !== 'function'
    ) {
      return false;
    }

    pageTracking.consentApiRequested = true;
    shopifyObject.loadFeatures([{
      name: 'consent-tracking-api',
      version: '0.1',
      onLoad() {
        const granted = resolveTrackingConsentGranted();
        dispatchConsentChanged(granted);
      }
    }]);

    return true;
  }

  pageTracking.reportCommerceInteraction = function reportCommerceInteraction() {
    return false;
  };
  pageTracking.collectCommerceInteractionFields = function collectCommerceInteractionFields() {
    return null;
  };

  function flushPendingNewsletterReport(coreInstance) {
    const pending =
      typeof window !== 'undefined' ? window.__pageTrackingPendingNewsletter : undefined;

    if (pending !== 'success' && pending !== 'fail') {
      return false;
    }

    const eventTypes = config.eventTypes || {};
    const typeName = eventTypes.newsletterSubscribe || '';

    if (!typeName || !coreInstance || typeof coreInstance.sendTrackingEvent !== 'function') {
      return false;
    }

    if (typeof window !== 'undefined') {
      window.__pageTrackingPendingNewsletter = '';
      delete window.__pageTrackingPendingNewsletter;
    }

    return Boolean(
      coreInstance.sendTrackingEvent(
        {
          basicInfo: coreInstance.buildBasicInfo(),
          dataInfo: coreInstance.buildDataInfo(
            {
              type: typeName,
              actionType: 'newsletter_subscribe',
              status: pending
            },
            new Date()
          )
        },
        false
      )
    );
  }

  function bootstrapPageTracking() {
    let core;
    let pageIdentity;
    let pageEnterAtMs;
    let sessionModule;
    let pageViewModule;
    let pageJumpModule;
    let elementExposureModule;
    let elementClickModule;
    let searchModule;
    let mediaInteractionModule;
    let popupExposureModule;
    let commerceInteractionModule;
    let sessionCallbacks;

    if (pageTracking.initialized) {
      return true;
    }

    if (!hasRequiredDependencies()) {
      return false;
    }

    try {
      core = createPageTrackingCore({
        window,
        document,
        config,
        trackingContext,
        utils
      });

      pageTracking.canTrack = core.canTrack; // 判断是否可以进行埋点
      pageTracking.sendTrackingEvent = core.sendTrackingEvent; // 发送埋点事件
      pageTracking.bootstrapPageTracking = bootstrapPageTracking;
      window.pageTracking = pageTracking;

      if (!core.canTrack()) {
        ensureConsentTrackingApiLoaded();
        return false;
      }

      pageIdentity = utils.resolvePageIdentity(trackingContext);
      pageEnterAtMs = Date.now();
      sessionModule = createPageTrackingSession(core);
      pageViewModule = createPageTrackingPageView(core, {
        pageIdentity: pageIdentity
      });
      pageJumpModule = createPageTrackingPageJump(core, {
        pageIdentity: pageIdentity,
        pageEnterAtMs: pageEnterAtMs
      });
      elementExposureModule = createPageTrackingElementExposure(core, {
        pageIdentity: pageIdentity
      });
      elementClickModule = createPageTrackingElementClick(core, {
        pageIdentity: pageIdentity
      });
      searchModule = createPageTrackingSearch(core);
      mediaInteractionModule = createPageTrackingMediaInteraction(core, {
        pageIdentity: pageIdentity
      });
      popupExposureModule = createPageTrackingPopupExposure(core, {
        pageIdentity: pageIdentity
      });
      commerceInteractionModule = createPageTrackingCommerceInteraction(core, {
        pageIdentity: pageIdentity
      });
      sessionCallbacks = {
        resetPageSessionState: pageViewModule.resetPageSessionState,
        scheduleInitialPageViewExposure: pageViewModule.scheduleInitialPageViewExposure,
        syncInitialScrollDepth: pageViewModule.syncInitialScrollDepth
      };

      sessionModule.initializeSession(sessionCallbacks);

      searchModule.bindSearchEvents();
      pageJumpModule.trackPendingPageJump();
      pageViewModule.bindLifecycleEvents(sessionModule, pageJumpModule);
      pageViewModule.scheduleInitialPageViewExposure();
      // 暂时关闭元素曝光上报，避免调试其他事件时请求噪声过多。
      // 需要恢复时，放开下一行即可。
      elementExposureModule.scheduleObservation(sessionModule, sessionCallbacks);
      elementClickModule.bindClickEvents(sessionModule, sessionCallbacks);
      mediaInteractionModule.bindMediaEvents(sessionModule, sessionCallbacks);
      popupExposureModule.bindPopupExposureEvents(sessionModule, sessionCallbacks);
      pageTracking.reportCommerceInteraction = function reportCommerceInteraction(fields) {
        return commerceInteractionModule.reportCommerceInteractionFields(
          fields,
          sessionModule,
          sessionCallbacks
        );
      };
      pageTracking.collectCommerceInteractionFields = function collectCommerceInteractionFields() {
        return null;
      };
      pageTracking.initialized = true;
      flushPendingNewsletterReport(core);

      return true;
    } catch (error) {
      // 初始化异常时静默失败，避免影响页面脚本
      return false;
    }
  }

  pageTracking.bootstrapPageTracking = bootstrapPageTracking;

  if (!pageTracking.consentListenerBound) {
    pageTracking.handleConsentChanged = function handleConsentChanged(event) {
      const detail = event && event.detail ? event.detail : {};

      if (detail.granted === true && window.pageTracking && typeof window.pageTracking.bootstrapPageTracking === 'function') {
        window.pageTracking.bootstrapPageTracking();
      }
    };
    document.addEventListener(consentChangedEventName, pageTracking.handleConsentChanged);
    pageTracking.consentListenerBound = true;
  }

  window.pageTracking = pageTracking;
  bootstrapPageTracking();
})(window, document);
